import { ToastPosition, useToaster } from "react-hot-toast/headless";
import styles from "./notifications.module.scss";
import { isMobile } from "react-device-detect";
import React, { useCallback, useId } from "react";

const ToastWrapper = ({
  id,
  className,
  style,
  onHeightUpdate,
  children,
}: {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  onHeightUpdate: (id: string, height: number) => void;
  children?: React.ReactNode;
}) => {
  const ref = useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        const updateHeight = () => {
          const height = el.getBoundingClientRect().height;
          onHeightUpdate(id, height);
        };
        updateHeight();
        new MutationObserver(updateHeight).observe(el, {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }
    },
    [id, onHeightUpdate]
  );

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

export const Notifications = () => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;
  const defaultPosition: ToastPosition = isMobile
    ? "bottom-center"
    : "top-right";
  const key = useId();

  const getPositionStyle = (
    position: ToastPosition,
    offset: number
  ): React.CSSProperties => {
    const top = position.includes("top");
    const verticalStyle: React.CSSProperties = top ? { top: 0 } : { bottom: 0 };
    const horizontalStyle: React.CSSProperties = position.includes("center")
      ? {
          justifyContent: "center",
        }
      : position.includes("right")
      ? {
          justifyContent: "flex-end",
        }
      : {};
    return {
      left: 0,
      right: 0,
      display: "flex",
      position: "absolute",
      transition: `all 230ms cubic-bezier(.21,1.02,.73,1)`,
      transform: `translateY(${offset * (top ? 1 : -1)}px)`,
      ...verticalStyle,
      ...horizontalStyle,
    };
  };

  return (
    <div
      className={styles.position}
      onMouseEnter={startPause}
      onMouseLeave={endPause}
      key={key}
    >
      {toasts.map((toast) => {
        const toastPosition = toast.position || defaultPosition;
        const offset = calculateOffset(toast, {
          reverseOrder: false,
          gutter: 8,
          defaultPosition: toastPosition,
        });
        const positionStyle = getPositionStyle(toastPosition, offset);

        return (
          <ToastWrapper
            id={toast.id}
            key={toast.id}
            onHeightUpdate={updateHeight}
            className={toast.visible ? styles.activeClass : ""}
            style={{ ...positionStyle, opacity: toast.visible ? 1 : 0 }}
          >
            {toast.message as any}
          </ToastWrapper>
        );
      })}
    </div>
  );
};
